import React, { useState, useEffect } from 'react';
import axios from '../axiosConfig';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Layout from './Layout';
import { Modal, Button } from 'react-bootstrap';

const API_URL = process.env.REACT_APP_API_URL;

function TriggerManagement() {
  const [triggers, setTriggers] = useState([]);
  const [forms, setForms] = useState([]);
  const [newTrigger, setNewTrigger] = useState({
    name: '',
    trigger_steps: '',
    enabled: true,
    organization_id: '',
    form_id: ''
  });

  const [editingTrigger, setEditingTrigger] = useState(null);
  const [showAddTriggerModal, setShowAddTriggerModal] = useState(false);
  const [showEditTriggerModal, setShowEditTriggerModal] = useState(false);

  useEffect(() => {
    fetchTriggers();
    fetchForms();
  }, []);

  const fetchTriggers = async () => {
    try {
      const response = await axios.get(`${API_URL}/triggers/app-triggers`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setTriggers(response.data);
    } catch (error) {
      toast.error('Failed to fetch triggers');
    }
  };

  const fetchForms = async () => {
    try {
      const response = await axios.get(`${API_URL}/forms`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setForms(response.data);
    } catch (error) {
      toast.error('Failed to fetch forms');
    }
  };

  const handleAddTrigger = async () => {
    try {
      await axios.post(`${API_URL}/triggers/app-triggers`, newTrigger, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setNewTrigger({ name: '', trigger_steps: '', enabled: true, organization_id: '', form_id: '' });
      setShowAddTriggerModal(false);
      fetchTriggers();
      toast.success('Trigger added successfully');
    } catch (error) {
      toast.error('Failed to add trigger');
    }
  };

  const handleDeleteTrigger = async (triggerId) => {
    try {
      await axios.delete(`${API_URL}/triggers/app-triggers/${triggerId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      fetchTriggers();
      toast.success('Trigger deleted successfully');
    } catch (error) {
      toast.error('Failed to delete trigger');
    }
  };

  const handleEditTrigger = async () => {
    try {
      await axios.put(`${API_URL}/triggers/app-triggers/${editingTrigger.id}`, editingTrigger, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setShowEditTriggerModal(false);
      fetchTriggers();
      toast.success('Trigger updated successfully');
    } catch (error) {
      toast.error('Failed to update trigger');
    }
  };

  const openEditModal = (trigger) => {
    setEditingTrigger(trigger);
    setShowEditTriggerModal(true);
  };

  return (
    <Layout>
      <div className="content-wrapper">
        <div className="content-header">
          <div className="container-fluid">
            <div className="row mb-2">
              <div className="col-sm-6">
                <h1 className="m-0">Trigger Management</h1>
              </div>
            </div>
          </div>
        </div>
        <div className="content">
          <div className="container-fluid">
            <div className="row mb-3">
              <div className="col-12">
                <Button variant="primary" onClick={() => setShowAddTriggerModal(true)}>
                  <i className="fas fa-plus"></i> Add Trigger
                </Button>
              </div>
            </div>
            <div className="row">
              <div className="col-12">
                <div className="card">
                  <div className="card-body">
                    <table className="table table-bordered">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Form Name</th>
                          <th>Trigger Steps</th>
                          <th>Enabled</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {triggers.map((trigger) => (
                          <tr key={trigger.id}>
                            <td>{trigger.name}</td>
                            <td>{trigger.form_name}</td>
                            <td>{trigger.trigger_steps}</td>
                            <td>{trigger.enabled ? "Yes" : "No"}</td>
                            <td>
                              <Button variant="warning" onClick={() => openEditModal(trigger)}>
                                <i className="fas fa-edit"></i> Edit
                              </Button>
                              <Button variant="danger" className="ml-2" onClick={() => handleDeleteTrigger(trigger.id)}>
                                <i className="fas fa-trash-alt"></i>
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add Trigger Modal */}
      <Modal show={showAddTriggerModal} onHide={() => setShowAddTriggerModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Add Trigger</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <input
            type="text"
            placeholder="Trigger Name"
            value={newTrigger.name}
            onChange={(e) => setNewTrigger({ ...newTrigger, name: e.target.value })}
            className="form-control mb-2"
          />

          {/* Form Dropdown */}
          <select
            className="form-control mb-2"
            value={newTrigger.form_id}
            onChange={(e) => setNewTrigger({ ...newTrigger, form_id: e.target.value })}
          >
            <option value="">Select Form</option>
            {forms.map((form) => (
              <option key={form.id} value={form.id}>{form.name}</option>
            ))}
          </select>

          <textarea
            placeholder="Trigger Steps (JSON Format)"
            value={newTrigger.trigger_steps}
            onChange={(e) => setNewTrigger({ ...newTrigger, trigger_steps: e.target.value })}
            className="form-control mb-2"
            rows={10}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddTriggerModal(false)}>Close</Button>
          <Button variant="primary" onClick={handleAddTrigger}>Save</Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Trigger Modal */}
      {editingTrigger && (
        <Modal show={showEditTriggerModal} onHide={() => setShowEditTriggerModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Edit Trigger</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <input
              type="text"
              placeholder="Trigger Name"
              value={editingTrigger.name}
              onChange={(e) => setEditingTrigger({ ...editingTrigger, name: e.target.value })}
              className="form-control mb-2"
            />

            {/* Form Dropdown */}
            <select
              className="form-control mb-2"
              value={editingTrigger.form_id}
              onChange={(e) => setEditingTrigger({ ...editingTrigger, form_id: e.target.value })}
            >
              <option value="">Select Form</option>
              {forms.map((form) => (
                <option key={form.id} value={form.id}>{form.name}</option>
              ))}
            </select>

            <textarea
              placeholder="Trigger Steps (JSON Format)"
              value={editingTrigger.trigger_steps}
              onChange={(e) => setEditingTrigger({ ...editingTrigger, trigger_steps: e.target.value })}
              className="form-control mb-2"
              rows={10}
            />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowEditTriggerModal(false)}>Close</Button>
            <Button variant="primary" onClick={handleEditTrigger}>Save Changes</Button>
          </Modal.Footer>
        </Modal>
      )}

      <ToastContainer position="top-right" autoClose={5000} />
    </Layout>
  );
}

export default TriggerManagement;
